import { useRoute } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  ArchiveRestore,
  Bell,
  BellRing,
  ChevronRight,
  LogOut,
  Menu,
  Settings,
  Tags,
  UserPlus,
  UserRoundCheck,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useEffect, useState } from "react";
import { Pressable } from "react-native";
import { Image, Text, View, XStack, YStack } from "tamagui";
import { LoginSheets } from "../../core/account/LoginSheets";
import { signOutFromApp } from "../../core/firebaseInit";
import { useStores } from "../../core/stores";
import { TagsModal } from "../../core/tags/TagsModal";
import { isRunningInApp } from "../../core/utils";
import { AccountSheetActive } from "../../plans/PlanModel";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { AnimationWiggle } from "../AnimationWiggle";
import { Body } from "../Body";
import { Group } from "../Group";
import { Sheet } from "../Sheet";
import { Title } from "../Title";
import { AvatarSize, UserAvatar } from "../UserAvatar";
import { ActivitySheet } from "./ActivitySheet";
import { useSharedModuleMenuOptions } from "./ModuleHeader";
import { ShareSheet } from "./ShareSheet";

export const PlanHeader: React.FC = observer(() => {
  const { sessionStore, plansStore } = useStores();
  const route =
    useRoute<
      NativeStackScreenProps<ReactNavigation.RootParamList, "Plan">["route"]
    >();
  const [showMenuSheet, setShowMenuSheet] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showActivitySheet, setActivitySheet] = useState(false);

  const plan = plansStore.byId.get(route.params.planUid);

  const navigation = usePlanNavigation();
  const userUid = plan?.userInPlan?.uid;

  useEffect(() => {
    if (plan?.title) {
      navigation.setOptions({ title: `${plan.title} - Planira` });
    }
  }, [plan?.title, navigation]);

  const sharedMenuItems = useSharedModuleMenuOptions({
    module: plan?.isOnlyOneModule,
    onHideSheet: () => {
      setShowMenuSheet(false);
    },
  });

  if (!userUid) {
    return null;
  }

  return (
    <>
      <YStack gap="$medium">
        <View backgroundColor={"$backgroundBlock"}>
          <XStack
            alignItems="stretch"
            gap="$medium"
            width="100%"
            justifyContent="space-between"
            padding="$space.large"
          >
            <XStack
              gap="$small"
              onPress={() => navigation.navigate("Home")}
              cursor="pointer"
            >
              <Image
                objectFit="contain"
                height={32}
                width={171}
                source={require("../../../assets/planira.png")}
              />
            </XStack>
            <XStack gap="$space.medium" alignItems="stretch">
              <Pressable
                onPress={() => setActivitySheet(true)}
                style={{ justifyContent: "center" }}
              >
                {plan?.hasNewActivity && (
                  <AnimationWiggle>
                    <BellRing color="$primary" />
                  </AnimationWiggle>
                )}
                {!plan?.hasNewActivity && (
                  <Bell color={plan?.hasNewActivity ? "$primary" : undefined} />
                )}
              </Pressable>
              <Pressable
                onPress={() => setShowMenuSheet(true)}
                style={{ justifyContent: "center" }}
              >
                <Menu />
              </Pressable>
            </XStack>
          </XStack>
        </View>
      </YStack>

      <Sheet
        sheetId="settings_plan"
        open={showMenuSheet}
        onOpenChange={setShowMenuSheet}
        header={<Title>Menu</Title>}
      >
        <Group borderTop={false}>
          <XStack
            width="100%"
            onPress={() => {
              setShowMenuSheet(false);
              navigation.navigate("PlanSettings", {
                planUid: route.params.planUid,
              });
            }}
            cursor="pointer"
            alignItems="center"
            gap="$medium"
          >
            <ChevronRight />
            <Settings />
            <Body>Settings</Body>
          </XStack>

          <XStack
            width="100%"
            onPress={() => {
              setShowMenuSheet(false);
              setShowTagsModal(true);
            }}
            cursor="pointer"
            alignItems="center"
            gap="$medium"
          >
            <ChevronRight />
            <Tags />
            <Body>Manage Tags</Body>
          </XStack>

          {sharedMenuItems}

          {!isRunningInApp() && (
            <XStack
              width="100%"
              onPress={() => {
                setShowMenuSheet(false);
                navigation.navigate("Profile", {
                  userUid: userUid ?? "",
                  planUid: route.params.planUid,
                });
              }}
              cursor="pointer"
              alignItems="center"
              gap="$medium"
            >
              <UserAvatar userUid={userUid} size={AvatarSize.Tiny} />
              <YStack gap="$small">
                <Body>Profile (You)</Body>
              </YStack>
            </XStack>
          )}

          <XStack
            width="100%"
            onPress={() => {
              plan.setShowShare(true);
              setShowMenuSheet(false);
            }}
            cursor="pointer"
            alignItems="center"
            gap="$medium"
          >
            <Text>
              <UserPlus />
            </Text>
            <Body>Members</Body>
          </XStack>

          <XStack
            width="100%"
            alignItems="center"
            gap="$medium"
            onPress={() => {
              setShowMenuSheet(false);
              navigation.navigate("Archive", {
                planUid: route.params.planUid,
              });
            }}
            cursor="pointer"
          >
            <Text>
              <ArchiveRestore />
            </Text>
            <Body>Restore items</Body>
          </XStack>

          {sessionStore.isRegistered && !isRunningInApp() && (
            <XStack
              width="100%"
              alignItems="center"
              gap="$medium"
              onPress={() => {
                signOutFromApp();
              }}
              cursor="pointer"
            >
              <Text>
                <LogOut />
              </Text>
              <Body>Sign out</Body>
            </XStack>
          )}
          {!sessionStore.isRegistered && !isRunningInApp() && (
            <XStack
              width="100%"
              alignItems="center"
              gap="$medium"
              onPress={() => {
                setShowMenuSheet(false);
                plan.setShowAccountSheet(AccountSheetActive.Register);
              }}
              cursor="pointer"
            >
              <Text>
                <UserRoundCheck />
              </Text>
              <Body>Register yourself</Body>
            </XStack>
          )}
        </Group>
      </Sheet>

      <ActivitySheet
        open={showActivitySheet}
        planUid={route.params.planUid}
        userUid={userUid ?? ""}
        onOpenChange={setActivitySheet}
      />
      {!!plan && (
        <>
          <ShareSheet planUid={plan.uid} />
          <LoginSheets
            currentSheet={plan.showAccountSheet}
            onChangeSheet={(s) => plan.setShowAccountSheet(s)}
          />
          <TagsModal
            planUid={plan.uid}
            visible={showTagsModal}
            onClose={() => setShowTagsModal(false)}
          />
        </>
      )}
    </>
  );
});
